import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Navigation from "../components/Navigation";
import { toast } from "sonner";
import {
  Radar as RadarIcon,
  Search,
  TrendingUp,
  Users,
  Newspaper,
  Lightbulb,
  ExternalLink,
  RefreshCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAMPLES = [
  "Solar panel manufacturing",
  "Hospital network",
  "Crypto exchange",
  "Defense contractor",
  "Family farm co-op",
  "EV charging startup",
  "Community bank",
  "Pharmaceutical R&D",
];

const LOADING_STAGES = [
  "Mapping your industry to federal policy areas…",
  "Scanning the current Congress for matching bills…",
  "Ranking by modeled pass probability…",
  "Writing your decision brief…",
];

const partyColor = (p) =>
  p === "R" || p === "Republican"
    ? "text-red-400 border-red-500/40 bg-red-500/10"
    : p === "D" || p === "Democratic"
    ? "text-blue-400 border-blue-500/40 bg-blue-500/10"
    : "text-white/60 border-white/20 bg-white/5";

const probColor = (p) =>
  p >= 0.7 ? "text-emerald-400" : p >= 0.4 ? "text-orange-400" : "text-white/50";

export default function Radar() {
  const [industry, setIndustry] = useState(() => localStorage.getItem("whip.radar.industry") || "");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(0);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setStage((s) => Math.min(s + 1, LOADING_STAGES.length - 1)), 3500);
    return () => clearInterval(t);
  }, [loading]);

  const analyze = async (value, force = false) => {
    const input = (value ?? industry).trim();
    if (input.length < 3) {
      toast.error("Describe your industry or product first");
      return;
    }
    setLoading(true);
    setStage(0);
    setReport(null);
    localStorage.setItem("whip.radar.industry", input);
    try {
      const r = await axios.post(`${API}/radar/analyze`, { industry: input, force }, { timeout: 120000 });
      if (r.data?.error) {
        toast.error(r.data.error);
      } else {
        setReport(r.data);
      }
    } catch (e) {
      toast.error("Radar analysis failed, please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg)" }}>
      <Navigation />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header + input */}
        <div className="mb-10">
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight flex items-center gap-3">
            <RadarIcon className="w-9 h-9 text-orange-400" />
            WHIP Radar
          </h1>
          <p className="text-white/55 mt-2 max-w-2xl">
            Tell WHIP what your organization does. Get back the bills most likely to pass that
            materially affect you, plus the legislators, themes, and coverage to watch.
          </p>

          <form
            className="mt-6 flex flex-col sm:flex-row gap-3 max-w-3xl"
            onSubmit={(e) => {
              e.preventDefault();
              analyze();
            }}
          >
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g. Regional hospital network, solar manufacturer, crypto exchange…"
                className="w-full bg-white/[0.04] border border-white/15 focus:border-orange-500/60 rounded-lg pl-9 pr-3 py-3 text-white placeholder-white/35 outline-none transition-colors"
                data-testid="radar-industry-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 rounded-lg font-bold bg-orange-500 hover:bg-orange-400 text-white transition-colors disabled:opacity-50"
              data-testid="radar-analyze-btn"
            >
              {loading ? "Analyzing…" : "Run Radar"}
            </button>
          </form>

          <div className="mt-3 flex flex-wrap gap-2" data-testid="radar-examples">
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => {
                  setIndustry(ex);
                  analyze(ex);
                }}
                className="text-xs px-2.5 py-1 rounded-full border border-white/15 text-white/55 hover:text-orange-200 hover:border-orange-500/40 transition-colors"
                data-testid={`radar-example-${ex.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
              >
                {ex}
              </button>
            ))}
          </div>
        </div>

        {/* Loading stages */}
        {loading && (
          <div className="text-center py-16" data-testid="radar-loading">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-5" />
            <p className="text-white font-semibold">{LOADING_STAGES[stage]}</p>
            <p className="text-xs text-white/40 mt-2">First run for a sector takes ~15-30 seconds; repeat runs are instant.</p>
          </div>
        )}

        {/* Report, "Control Room" bento grid */}
        {!loading && report && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="radar-report">
            {/* Mission Brief headline */}
            <section className="md:col-span-12 whip-hero-fade">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="whip-pill whip-pill-orange" data-testid="radar-sector-pill">{report.sector}</span>
                {(report.policyAreas || []).map((a) => (
                  <span key={a} className="whip-pill">{a}</span>
                ))}
                <span className="text-[11px] font-mono text-white/40 ml-1">
                  {report.matchedCount} MATCHED · {report.inProgressCount} IN PLAY
                  {report.cached && " · CACHED"}
                </span>
                <button
                  type="button"
                  onClick={() => analyze(report.industry, true)}
                  className="text-[11px] font-mono text-white/40 hover:text-orange-300 inline-flex items-center gap-1 transition-colors"
                  data-testid="radar-refresh"
                  title="Re-run the analysis with fresh data"
                >
                  <RefreshCw className="w-3 h-3" /> REFRESH
                </button>
              </div>
              <div className="border-l-2 pl-4 sm:pl-5" style={{ borderColor: "#FF8A00" }}>
                <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-orange-400 mb-1.5">
                  Radar Brief · {(report.generatedAt || "").slice(0, 10)}
                </p>
                <p
                  className="text-base sm:text-lg text-white/85 font-medium leading-relaxed max-w-3xl"
                  data-testid="radar-headline"
                >
                  {report.headline}
                </p>
              </div>
            </section>

            {/* Main event: ranked bills */}
            <section className="md:col-span-12 lg:col-span-8" data-testid="radar-top-bills">
              {/* NOTE: no overflow-hidden here, the forecast-driver hover
                  popover must be able to escape the container bounds. */}
              <div className="whip-surface rounded-xl border border-white/10">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-white/[0.02]">
                  <h3 className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60 flex items-center gap-2">
                    <TrendingUp className="w-3.5 h-3.5 text-orange-400" /> Bills to Watch
                  </h3>
                  <span className="text-[11px] font-mono text-white/35">RANKED BY PASS PROBABILITY</span>
                </div>
                {(report.topBills || []).map((b, i) => (
                  <Link key={b.billId} to={`/bill/${b.billId}`} className="block group relative hover:z-50">
                    <div
                      className="px-5 py-4 hover:bg-orange-500/[0.05] transition-colors whip-hero-fade"
                      style={{ animationDelay: `${i * 90}ms` }}
                      data-testid={`radar-bill-${i + 1}`}
                    >
                      <div className="flex items-start gap-4">
                        <span className="text-3xl font-black font-mono text-white/15 leading-none pt-1 w-8 shrink-0">
                          {i + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="text-xs font-mono font-bold text-orange-300">{b.billNumber}</span>
                            {b.policyArea && <span className="whip-pill !py-0.5 !px-2 !text-[9px]">{b.policyArea}</span>}
                            {b.verdict && (
                              <span className="whip-pill whip-pill-orange !py-0.5 !px-2 !text-[9px]">{b.verdict}</span>
                            )}
                          </div>
                          <p className="text-white font-bold leading-snug group-hover:text-orange-200 transition-colors">
                            {b.title}
                          </p>
                          {b.whyItMatters && (
                            <p className="text-sm text-white/55 mt-1.5 leading-relaxed">
                              <span className="text-orange-300/90 font-semibold">Why it matters: </span>
                              {b.whyItMatters}
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0 w-24 relative group/prob">
                          {b.passProbability !== null && b.passProbability !== undefined ? (
                            <>
                              <div className={`text-4xl font-black font-mono tabular-nums leading-none ${probColor(b.passProbability)}`}>
                                {Math.round(b.passProbability * 100)}
                                <span className="text-lg align-top">%</span>
                              </div>
                              <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
                                <div
                                  className="h-full rounded-full"
                                  style={{
                                    width: `${Math.max(4, b.passProbability * 100)}%`,
                                    background: "linear-gradient(90deg, #FF6A00, #FFA500)",
                                    boxShadow: "0 0 8px rgba(255,138,0,0.55)",
                                  }}
                                />
                              </div>
                              <div className="text-[9px] font-mono text-white/35 uppercase tracking-wider mt-1">
                                pass prob{b.forecastDetail ? " ⓘ" : ""}
                              </div>
                              {/* Δ since last run, shown when this bill also
                                  appeared on the previous report for the sector */}
                              {b.previousProbability !== null &&
                                b.previousProbability !== undefined &&
                                Math.round(b.passProbability * 100) !== Math.round(b.previousProbability * 100) && (
                                <div
                                  className={`text-[10px] font-mono font-bold mt-0.5 ${
                                    b.passProbability > b.previousProbability ? "text-emerald-400" : "text-rose-400"
                                  }`}
                                  data-testid={`radar-bill-delta-${i + 1}`}
                                  title={`Was ${Math.round(b.previousProbability * 100)}% on your ${b.previousDate} report`}
                                >
                                  {b.passProbability > b.previousProbability ? "▲" : "▼"} from{" "}
                                  {Math.round(b.previousProbability * 100)}%
                                </div>
                              )}
                              {/* Forecast drivers, hover breakdown */}
                              {b.forecastDetail && (
                                <div
                                  className={`hidden group-hover/prob:block absolute right-0 ${
                                    i >= (report.topBills || []).length - 2
                                      ? "bottom-full mb-1"
                                      : "top-full mt-1"
                                  } w-72 z-50 rounded-lg border border-orange-500/35 p-3.5 text-left shadow-2xl`}
                                  style={{ background: "var(--whip-surface-2, #1A1A25)" }}
                                  data-testid={`radar-forecast-detail-${i + 1}`}
                                >
                                  <p className="text-[10px] font-mono font-bold uppercase tracking-[0.18em] text-orange-400 mb-2">
                                    What's driving this number
                                  </p>
                                  <div className="space-y-1.5 text-xs">
                                    {b.forecastDetail.expectedYes !== null && b.forecastDetail.expectedYes !== undefined && (
                                      <div className="flex justify-between gap-2">
                                        <span className="text-white/50">Expected votes ({b.forecastDetail.forecastChamber})</span>
                                        <span className="font-mono text-white/85">
                                          {Math.round(b.forecastDetail.expectedYes)}Y, {Math.round(b.forecastDetail.expectedNo)}N
                                          <span className="text-white/40"> / {b.forecastDetail.threshold} needed</span>
                                        </span>
                                      </div>
                                    )}
                                    <div className="flex justify-between gap-2">
                                      <span className="text-white/50">Reported out of committee</span>
                                      <span className={b.forecastDetail.committeeReported ? "text-emerald-400 font-semibold" : "text-white/40"}>
                                        {b.forecastDetail.committeeReported ? "Yes ↑" : "Not yet"}
                                      </span>
                                    </div>
                                    <div className="flex justify-between gap-2">
                                      <span className="text-white/50">Passed originating chamber</span>
                                      <span className={b.forecastDetail.passedOriginatingChamber ? "text-emerald-400 font-semibold" : "text-white/40"}>
                                        {b.forecastDetail.passedOriginatingChamber ? "Yes ↑" : "Not yet"}
                                      </span>
                                    </div>
                                    <div className="flex justify-between gap-2">
                                      <span className="text-white/50">Leadership endorsed</span>
                                      <span className={b.forecastDetail.leadershipEndorsed ? "text-emerald-400 font-semibold" : "text-white/40"}>
                                        {b.forecastDetail.leadershipEndorsed ? "Yes ↑" : "No"}
                                      </span>
                                    </div>
                                    <div className="flex justify-between gap-2">
                                      <span className="text-white/50">Cosponsors</span>
                                      <span className="font-mono text-white/85">
                                        {b.forecastDetail.cosponsorCount ?? 0}
                                        {(b.forecastDetail.crossPartyCosponsors ?? 0) > 0 && (
                                          <span className="text-emerald-400"> ({b.forecastDetail.crossPartyCosponsors} cross-party ↑)</span>
                                        )}
                                      </span>
                                    </div>
                                    <div className="flex justify-between gap-2">
                                      <span className="text-white/50">Momentum</span>
                                      <span className={b.forecastDetail.stalled ? "text-rose-400 font-semibold" : "text-white/85 font-mono"}>
                                        {b.forecastDetail.stalled
                                          ? `Stalled ${b.forecastDetail.daysSinceLastAction}d ↓`
                                          : `Active ${b.forecastDetail.daysSinceLastAction ?? "n/a"}d ago`}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-[10px] font-mono text-white/35 uppercase tracking-wider pt-3">
                              forecast<br />pending
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {i < (report.topBills || []).length - 1 && <div className="whip-divider" />}
                  </Link>
                ))}
                {(report.topBills || []).length === 0 && (
                  <p className="text-white/50 text-sm px-5 py-8">
                    No in-progress bills matched this sector in the current Congress.
                  </p>
                )}
              </div>
            </section>

            {/* Sidebar: people + themes */}
            <aside className="md:col-span-12 lg:col-span-4 space-y-6" data-testid="radar-sidebar">
              <div className="whip-surface rounded-xl border border-white/10 overflow-hidden" data-testid="radar-legislators">
                <div className="px-4 py-3 border-b border-white/10 bg-white/[0.02]">
                  <h3 className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60 flex items-center gap-2">
                    <Users className="w-3.5 h-3.5 text-orange-400" /> Legislators to Watch
                  </h3>
                </div>
                {(report.legislators || []).map((l, i) => {
                  return (
                  <Link key={l.bioguideID} to={`/legislator/${l.id}`} className="block group">
                    <div
                      className="px-4 py-2.5 flex items-center gap-3 hover:bg-orange-500/[0.05] transition-colors whip-hero-fade"
                      style={{ animationDelay: `${200 + i * 70}ms` }}
                      data-testid={`radar-legislator-${l.bioguideID}`}
                    >
                      <div
                        className="w-10 h-10 rounded-full overflow-hidden shrink-0 border-2 bg-white/5 flex items-center justify-center"
                        style={{
                          borderColor:
                            l.party === "D" || l.party === "Democratic"
                              ? "var(--whip-dem, #2F6BFF)"
                              : l.party === "R" || l.party === "Republican"
                              ? "var(--whip-rep, #FF3B3B)"
                              : "rgba(255,255,255,0.25)",
                        }}
                      >
                        {l.photo ? (
                          <img
                            src={l.photo}
                            alt={l.name}
                            className="w-full h-full object-cover object-top"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <span className="text-xs font-bold text-white/60">
                            {(l.name || "?").replace(/^(Sen\.|Rep\.)\s*/, "").slice(0, 2).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-semibold truncate group-hover:text-orange-200 transition-colors">
                          {(l.name || "").replace(/\s*\[.*\]\s*$/, "")}
                        </p>
                        <p className="text-[11px] text-white/40 truncate font-mono">
                          {l.party}-{l.state} · {l.billNumber}
                        </p>
                        {/* Legislator intel, appears on hover, same pattern
                            as the Legislative Process step expansions */}
                        <p
                          className="hidden group-hover:block text-xs text-orange-300/90 mt-1.5 italic leading-relaxed border-l-2 border-orange-500/40 pl-2 whitespace-normal"
                          data-testid={`radar-legislator-why-${l.bioguideID}`}
                        >
                          {l.insight
                            ? l.insight
                            : "Analyst dossier still compiling, hit REFRESH above to regenerate this brief."}
                        </p>
                      </div>
                    </div>
                    {i < (report.legislators || []).length - 1 && <div className="whip-divider" />}
                  </Link>
                  );
                })}
                {(report.legislators || []).length === 0 && (
                  <p className="text-white/50 text-sm px-4 py-6">Sponsor data unavailable.</p>
                )}
              </div>

              <div data-testid="radar-news" className="whip-surface rounded-xl border border-white/10 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 bg-white/[0.02]">
                  <h3 className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60 flex items-center gap-2">
                    <Newspaper className="w-3.5 h-3.5 text-orange-400" /> In the Press
                  </h3>
                </div>
                {(report.news || []).slice(0, 6).map((n, i) => (
                  <a key={n.link || `news-${i}`} href={n.link} target="_blank" rel="noreferrer" className="block group" data-testid={`radar-news-${i + 1}`}>
                    <div
                      className="px-4 py-2.5 hover:bg-orange-500/[0.05] transition-colors whip-hero-fade"
                      style={{ animationDelay: `${350 + i * 60}ms` }}
                    >
                      <p className="text-[10px] font-mono text-orange-400/80 uppercase tracking-wider mb-0.5">
                        {n.source} · {(n.publishedAt || "").slice(0, 10)}
                      </p>
                      <p className="text-white/85 text-[13px] font-medium leading-snug group-hover:text-orange-200 transition-colors">
                        {n.title} <ExternalLink className="w-3 h-3 inline -mt-0.5 opacity-40" />
                      </p>
                    </div>
                    {i < Math.min((report.news || []).length, 6) - 1 && <div className="whip-divider" />}
                  </a>
                ))}
                {(report.news || []).length === 0 && (
                  <p className="text-white/50 text-sm px-4 py-6">No recent coverage found.</p>
                )}
              </div>

            </aside>

            {/* Sector Themes, full-width strip */}
            <section className="md:col-span-12" data-testid="radar-themes">
              <div className="flex items-center gap-2 mb-3 px-1">
                <h3 className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60 flex items-center gap-2">
                  <Lightbulb className="w-3.5 h-3.5 text-orange-400" /> Sector Themes
                </h3>
                <span className="text-[11px] font-mono text-white/35">· WHAT'S DRIVING YOUR SECTOR ON THE HILL</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                {(report.themes || []).map((t, i) => (
                  <div
                    key={t.title || `theme-${i}`}
                    className="flex-1 min-w-0 rounded-lg border border-white/10 p-4 whip-hero-fade hover:border-orange-500/35 transition-colors"
                    style={{ background: "var(--whip-surface-2, #1A1A25)", animationDelay: `${450 + i * 80}ms` }}
                    data-testid={`radar-theme-${i + 1}`}
                  >
                    <span className="text-2xl font-black font-mono text-orange-500/60 leading-none block mb-2">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-white font-bold text-sm leading-snug">{t.title}</p>
                    <p className="text-white/50 text-xs mt-1.5 leading-relaxed">{t.detail}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {/* Empty state */}
        {!loading && !report && (
          <div
            className="text-center py-16 rounded-xl border border-dashed border-white/15 bg-white/[0.02]"
            data-testid="radar-empty-state"
          >
            <RadarIcon className="w-10 h-10 text-orange-400/60 mx-auto mb-4" />
            <p className="text-lg font-bold text-white mb-2">Point Radar at your world</p>
            <p className="text-sm text-white/55 max-w-md mx-auto">
              Instead of "here are today's bills," WHIP answers: <em className="text-orange-200 not-italic font-semibold">
              "these are the bills most likely to pass that materially affect your organization."</em> Try an example above.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
